import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    SettingState,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import * as tios from './tios';
import * as simulator from './simulator';


export default class ModbusMaster extends BaseFeature {
    constructor() {
        super();
        this.name = 'Modbus Master';
        this.id = 'modbus_master';
        this.icon = 'BsHddNetwork';
        this.requirements = ['tios'];

        const settings = [
            {
                id: 'DEBUG_PRINT',
                name: 'Debug Printing',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'ON',
                        value: '1',
                    },
                    {
                        name: 'OFF',
                        value: '0',
                    },
                ],
            },
            {
                id: 'TIMEOUT',
                name: 'Timeout (ms)',
                settingType: SettingType.NUMBER,
                required: true,
                default: '500',
            },
            {
                id: 'FRAME_DELAY',
                name: 'Frame Delay (ms)',
                description: 'The delay between frames. This is used to limit the number of requests that can be sent to the slave devices.',
                settingType: SettingType.NUMBER,
                required: true,
                default: '2',
            },
            {
                id: 'MAX_REQUESTS',
                name: 'Max Requests',
                description: 'The maximum number of requests that can be queued up. This is used to limit the number of requests that can be sent to the slave devices.',
                settingType: SettingType.NUMBER,
                required: true,
                default: '20',
            },
            {
                id: 'MODBUSDEVICES',
                name: 'Modbus Slaves',
                settingType: SettingType.ARRAY,
                refId: 'modbus_device',
                required: true,
                default: [] as BaseFeature[],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getEnabled(): boolean {
        const MODBUSDEVICES = this.getSettingValue('MODBUSDEVICES');
        if (MODBUSDEVICES.length > 0) {
            return true;
        }
        for (let i = 0; i < this.project.features.length; i += 1) {
            const feature = this.project.features[i];
            if (feature.id === 'node_modbus_write') {
                return true;
            }
        }
        return false;
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];
        const MODBUSDEVICES = this.getSettingValue('MODBUSDEVICES');
        const MODBUS_TIMEOUT = this.getSettingValue('TIMEOUT');
        const MODBUS_MAX_REQUESTS = this.getSettingValue('MAX_REQUESTS');
        const MODBUS_FRAME_DELAY_MS = this.getSettingValue('FRAME_DELAY');
        if (this.getSettingValue('DEBUG_PRINT').toString() === '1') {
            defines.push({
                name: 'MODBUS_DEBUG_PRINT',
                value: '1',
            });
        }
        defines.push({
            name: 'MODBUS_TIMEOUT',
            value: MODBUS_TIMEOUT.toString(),
        });
        let registerCount = 0;
        MODBUSDEVICES.forEach((device: BaseFeature) => {
            const registers = device.getSettingValue('REGISTERS');
            registerCount += registers.length;
        });
        for (let i = 0; i < this.project.features.length; i += 1) {
            const feature = this.project.features[i];
            if (feature.id === 'node_modbus_write') {
                registerCount++;
            }
        }
        defines.push({
            name: 'MODBUS_MAX_REQUESTS',
            value: (MODBUS_MAX_REQUESTS).toString(),
        });
        defines.push({
            name: 'MODBUS_FRAME_DELAY_MS',
            value: (MODBUS_FRAME_DELAY_MS).toString(),
        });
        return defines;
    }

    getDependencies() {
        const dependencies = [
            'modbus',
        ];

        return dependencies;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        switch (settingId) {
            case 'MASTER_SERIAL_PORT':
            case 'SLAVE_SERIAL_PORT':
                return project.getModbusSerialPorts();
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getState(settingId: string): SettingState {
        switch (settingId) {
            case 'MASTER_SERIAL_PORT':
                return this.getSettingValue('SLAVEPROTOCOL') === 'modbus_TCP' ? SettingState.HIDDEN : SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): Array<CodeBlock> {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `
## Modbus Master
The device can act as a Modbus master, supporting TCP and RTU protocols.

Slave devices can be added to the device, and configured registers for the device will be polled at the configured interval.

Slave device register values will then be accessible as **variables**.
        `;
    }
}
